import { isBuildingPropertyUnit } from "../../../../../../../../../../../../../_shared/_logic/units"
import type { BacklogListImport } from "../../../../../_shared/_logic/batch-upload-contract"

type BacklogListRow = BacklogListImport["rows"][number]
type BacklogListSourceSystem = BacklogListImport["sourceSystem"]

const normalizedUnitNumber = (value: string | null | undefined) => `${value ?? ""}`.trim()

export const exactBacklogUnitIds = (property: Property, customerNumber: string): string[] => {
  const normalizedCustomerNumber = normalizedUnitNumber(customerNumber)

  if (!normalizedCustomerNumber) return []

  const matchingUnits = (property.units ?? []).filter(
    (unit) => normalizedUnitNumber(unit.unit_number) === normalizedCustomerNumber,
  )

  return matchingUnits.length === 1 ? [`${matchingUnits[0].id}`] : []
}

const validatedServerUnitId = (property: Property, row: BacklogListRow): string | null => {
  if (row.property_unit_id === undefined || row.property_unit_id === null) return null

  const unitId = `${row.property_unit_id}`

  return (property.units ?? []).some((unit) => `${unit.id}` === unitId) ? unitId : null
}

export const orderBatchPropertyUnits = (units: PropertyUnit[] | null | undefined): PropertyUnit[] =>
  (units ?? [])
    .map((unit, index) => ({ unit, index }))
    .sort((left, right) => {
      const typeOrder =
        Number(!isBuildingPropertyUnit(left.unit)) - Number(!isBuildingPropertyUnit(right.unit))

      return typeOrder || left.index - right.index
    })
    .map(({ unit }) => unit)

export const soleBuildingUnitId = (property: Property): string | null => {
  const units = property.units ?? []

  return units.length === 1 && isBuildingPropertyUnit(units[0]) ? `${units[0].id}` : null
}

/** ISI describes a whole property; select only one existing building unit, never invent one. */
export const backlogAddressUnitId = (
  property: Property,
  sourceSystem: BacklogListSourceSystem,
): string | null => {
  if (sourceSystem !== "isi_export") return soleBuildingUnitId(property)

  const units = (property.units ?? []).filter(isBuildingPropertyUnit)

  return units.length === 1 ? String(units[0].id) : null
}

export const backlogUnitIdsByPropertyId = ({
  properties,
  rows,
  sourceSystem,
}: {
  properties: Property[]
  rows: BacklogListRow[]
  sourceSystem: BacklogListSourceSystem
}): Record<string, string[]> => {
  const propertyById = new Map(properties.map((property) => [property.id, property]))
  const selectedIdsByPropertyId = new Map<string, Set<string>>()

  rows.forEach((row) => {
    if (row.status !== "matched" || !row.property_id) return

    const property = propertyById.get(row.property_id)

    if (!property) return

    if (
      ["portal_kamin_futura", "portal_dexheimer_agzess", "isi_export"].includes(sourceSystem) &&
      row.fields.address != null &&
      row.property_unit_id == null
    ) {
      const selectedIds = selectedIdsByPropertyId.get(property.id) ?? new Set<string>()
      const buildingUnitId = backlogAddressUnitId(property, sourceSystem)

      if (buildingUnitId) selectedIds.add(buildingUnitId)

      selectedIdsByPropertyId.set(property.id, selectedIds)

      return
    }

    const serverUnitId = validatedServerUnitId(property, row)

    if (row.property_unit_id !== undefined && row.property_unit_id !== null && !serverUnitId) return

    const matchingUnitIds = serverUnitId
      ? [serverUnitId]
      : sourceSystem === "portal_uno_office"
        ? exactBacklogUnitIds(property, row.fields.customer_number)
        : []

    if (matchingUnitIds.length !== 1) return

    const selectedIds = selectedIdsByPropertyId.get(property.id) ?? new Set<string>()
    selectedIds.add(matchingUnitIds[0])
    selectedIdsByPropertyId.set(property.id, selectedIds)
  })

  return Object.fromEntries(
    properties.flatMap((property) => {
      const selectedIds = selectedIdsByPropertyId.get(property.id)

      if (!selectedIds) return []

      return [
        [
          property.id,
          orderBatchPropertyUnits(property.units)
            .map((unit) => `${unit.id}`)
            .filter((unitId) => selectedIds.has(unitId)),
        ],
      ]
    }),
  )
}

export const mergeBacklogUnitSelections = ({
  current,
  previousBacklog,
  incomingBacklog,
  removedPropertyIds,
  manualPropertyIds = [],
}: {
  current: Record<string, string[]>
  previousBacklog: Record<string, string[]>
  incomingBacklog: Record<string, string[]>
  removedPropertyIds: string[]
  manualPropertyIds?: string[]
}): Record<string, string[]> => {
  const next = Object.fromEntries(
    Object.entries(current).map(([propertyId, unitIds]) => [propertyId, [...unitIds]]),
  )
  const manual = new Set(manualPropertyIds)

  Object.entries(previousBacklog).forEach(([propertyId, unitIds]) => {
    if (manual.has(propertyId)) return

    const previousIds = new Set(unitIds)
    const remainingIds = (next[propertyId] ?? []).filter((unitId) => !previousIds.has(`${unitId}`))

    if (remainingIds.length) {
      next[propertyId] = remainingIds
    } else {
      delete next[propertyId]
    }
  })
  removedPropertyIds.forEach((propertyId) => delete next[propertyId])
  Object.entries(incomingBacklog).forEach(([propertyId, unitIds]) => {
    if (manual.has(propertyId)) return

    if (unitIds.length)
      next[propertyId] = Array.from(new Set([...(next[propertyId] ?? []), ...unitIds]))
    else if (!(propertyId in next)) next[propertyId] = []
  })

  return next
}
